import * as React from 'react';

import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

import Menu from '@components/menu';
import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import CloseIcon from '@components/basics/icons/close';
import DotsCircleIcon from '@components/basics/icons/dots-circle';
import AppState from '@webapp/store/state';

interface NotificationRowCtrlProps {
  className?: string;
  isRead: boolean;
  imgSrc?: string;
  imgAlt?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  timestamp: string;
  isOpen?: boolean;
  hasOpen?: boolean;
  id: string;
  isMobile: boolean;
  clearNotification: (id) => void;
  notificationClick?: () => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

export const NotificationRowCtrl: React.FC<NotificationRowCtrlProps> = ({
  className,
  isRead,
  imgSrc,
  imgAlt,
  children,
  actions,
  timestamp,
  isOpen,
  id,
  isMobile,
  clearNotification,
  notificationClick,
}) => {
  const theme = useTheme();

  return (
    <NotificationRow
      isOpen={isOpen}
      className={className}
      gap={isMobile ? 10 : 20}
      onClick={notificationClick}
    >
      {imgSrc && (
        <CircleImg
          src={imgSrc}
          alt={imgAlt ? imgAlt : `${(<Translate id="NOTIFICATION.IMG.ALT" />)}`}
        />
      )}
      <ContentWrapper>
        <Grid gap={5}>
          <Flex justifyContent="flex-start" alignItems="flex-start" gap={5}>
            {!isRead && <RedDot />}
            {children}
          </Flex>
          <Typography type="small" color={theme.colors.tertiary}>
            {timestamp}
          </Typography>
        </Grid>
        <InfoWrapper gap={10}>
          {actions ? actions : null}
          <Menu
            className="menu"
            parentRelative
            button={<DotsCircleIcon className="open-icon" isWhite />}
            closeButton={<CloseIcon isWhite className="close-icon" />}
            customIconHeight="24px"
            menuId={`notification-menu-${id}`}
          >
            <MenuContent>
              <Typography
                weight="700"
                className="menu-item danger"
                onClick={() => clearNotification(id)}
              >
                <Translate id="NOTIFICATIONS.CLEAR" />
              </Typography>
            </MenuContent>
          </Menu>
        </InfoWrapper>
      </ContentWrapper>
    </NotificationRow>
  );
};

export default connect(mapStateToProps)(styled(NotificationRowCtrl)`
  .menu {
    .menu-action {
      z-index: 0;
    }

    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }
`);

const NotificationRow = styled(Flex)<{ isOpen?: boolean }>`
  padding: 20px;
  min-height: 90px;
  border-radius: ${({ isOpen }) => (isOpen ? '10px 10px 0 0' : '10px')};
  background-color: ${({ theme }) => theme.colors.box};
  align-items: flex-start;
  justify-content: space-between;
  transition: border-radius
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};
  margin-bottom: ${({ isOpen }) => (isOpen ? '0' : '8px')};

  &:last-child {
    margin-bottom: 0;
  }

  @media ${({ theme }) => theme.device.laptop} {
    position: relative;
    align-items: center;
    padding: 30px;
  }
`;

const ContentWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const InfoWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;

  @media ${({ theme }) => theme.device.laptop} {
    justify-content: flex-end;
    flex-shrink: 0;
    margin-top: 0;
  }
`;

const CircleImg = styled.img`
  border-radius: 50%;
  overflow: hidden;
  min-width: 32px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const RedDot = styled.span`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-block-start: 5px;
  background-color: ${({ theme }) => theme.colors.accent};
`;

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;
